import type { PendingStreamRecovery } from "@/lib/pendingStreamRecovery";
import type { PendingStreamGeneration } from "@/services/chatStreamService";

export type MergedPendingRecovery = PendingStreamRecovery & {
  source: "local" | "server" | "both";
};

const parseUpdatedAt = (updatedAt: string): number => {
  const parsed = Date.parse(updatedAt);
  return Number.isNaN(parsed) ? Date.now() : parsed;
};

const serverStatusToPhase = (
  status: string
): PendingStreamRecovery["phase"] => {
  if (status === "done") {
    return "persisting_done";
  }
  if (status === "error") {
    return "persisting_error";
  }
  return "streaming";
};

export const mergePendingRecoveries = (
  localEntries: PendingStreamRecovery[],
  serverEntries: PendingStreamGeneration[],
  userId: string,
  tenantId: string
): MergedPendingRecovery[] => {
  const merged = new Map<string, MergedPendingRecovery>();

  for (const entry of localEntries) {
    if (entry.userId !== userId || entry.tenantId !== tenantId) {
      continue;
    }
    merged.set(entry.generationId, { ...entry, source: "local" });
  }

  for (const serverEntry of serverEntries) {
    const existing = merged.get(serverEntry.generationId);
    if (existing) {
      const serverPhase = serverStatusToPhase(serverEntry.status);
      const conversationMismatch =
        existing.conversationId !== serverEntry.conversationId;

      merged.set(serverEntry.generationId, {
        ...existing,
        conversationId: serverEntry.conversationId,
        lastChunkSeq: Math.max(existing.lastChunkSeq, 0),
        persistedAt: Math.max(
          existing.persistedAt,
          parseUpdatedAt(serverEntry.updatedAt)
        ),
        phase: serverPhase,
        ...(conversationMismatch
          ? {
              streamContext: null,
              doneEvent: null,
              errorMessage: null,
              inFlightHumanMessageId: null,
              inFlightHumanMessagePersisted: false,
            }
          : {
              doneEvent:
                serverEntry.status === "done" ? existing.doneEvent : null,
              errorMessage:
                serverEntry.status === "error" ? existing.errorMessage : null,
            }),
        source: "both",
      });
      continue;
    }

    merged.set(serverEntry.generationId, {
      generationId: serverEntry.generationId,
      conversationId: serverEntry.conversationId,
      userId,
      tenantId,
      lastChunkSeq: 0,
      phase: serverStatusToPhase(serverEntry.status),
      streamContext: null,
      doneEvent: null,
      errorMessage: null,
      inFlightHumanMessageId: null,
      inFlightHumanMessagePersisted: false,
      persistedAt: parseUpdatedAt(serverEntry.updatedAt),
      source: "server",
    });
  }

  return Array.from(merged.values()).sort(
    (left, right) => left.persistedAt - right.persistedAt
  );
};
